<script lang="ts">
  import { scheduleUndo } from '$lib/state/actionDialog.svelte';
  // Hotels' main-nav replacement for Companies (see the Hotels nav
  // restructure spec) — a plain list of kind=owner companies, scoped to the
  // Hotels vertical, with a site count per row (see ownerSiteCounts on the
  // backend). Clicking a row reuses CompanyDetail's existing owner-rollup
  // branch rather than rebuilding it; Companies itself is untouched and
  // stays reachable (deep links, other verticals, Admin > Companies).
  import { createQuery, createMutation, useQueryClient } from '@tanstack/svelte-query';
  import { Landmark, Trash2, Pencil } from 'lucide-svelte';
  import { companiesApi } from '$lib/api/crm';
  import { session } from '$lib/state/session.svelte';
  import { route } from '$lib/state/route.svelte';
  import { getErrorMessage } from '$lib/api/client';
  import SearchBar from '$lib/components/ui/SearchBar.svelte';
  import Pagination from '$lib/components/ui/Pagination.svelte';
  import CompanyDetail from './CompanyDetail.svelte';
  import CompanyFormModal from './CompanyFormModal.svelte';
  import type { Company } from '$lib/types';

  const queryClient = useQueryClient();

  let search = $state('');
  let page = $state(1);
  let showForm = $state(false);
  let editingOwner = $state<Company | null>(null);

  // Same URL-as-state convention as CompaniesPage/SitesPage: the selected
  // owner id lives in the hash (#/owners/<id>), not local state.
  const detailOwnerId = $derived(route.current.tab === 'owners' ? (route.current.id ?? null) : null);

  const verticalId = $derived(typeof session.selectedVertical === 'number' ? session.selectedVertical : undefined);
  const verticalSlug = $derived(session.me?.verticals.find((v) => v.id === verticalId)?.slug);
  // Owners only exists for a single selected Hotels vertical — same
  // single-vertical gating idea as SitesPage, covering a direct #/owners
  // link or a vertical switch while already on this page.
  const ownersAvailable = $derived(verticalSlug === 'hotels');

  const listQuery = createQuery(() => ({
    queryKey: ['companies', 'owners-list', { search, page, verticalId }],
    queryFn: () =>
      companiesApi.list({ q: search || undefined, page, per_page: 25, kind: 'owner', vertical_id: verticalId }),
    enabled: ownersAvailable && verticalId !== undefined
  }));

  $effect(() => {
    search;
    verticalId;
    page = 1;
  });

  const deleteMutation = createMutation(() => ({
    mutationFn: (id: number) => companiesApi.remove(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['companies'] })
  }));

  function openCreate() {
    editingOwner = null;
    showForm = true;
  }
  function openEdit(row: Company) {
    editingOwner = row;
    showForm = true;
  }
  function closeForm() {
    showForm = false;
    editingOwner = null;
  }
  function openDetail(row: Company) {
    route.push({ tab: 'owners', id: row.id });
  }
  function closeDetail() {
    route.push({ tab: 'owners' });
  }
  function remove(row: Company) {
    scheduleUndo(`Delete owner #${row.id} “${row.name}”`, () => deleteMutation.mutate(row.id));
  }
</script>

{#if detailOwnerId}
  <CompanyDetail
    id={detailOwnerId}
    backLabel="Owners"
    onClose={closeDetail}
    onInvalid={() => route.replace({ tab: 'owners' })}
    onNavigate={(navId) => route.push({ tab: 'companies', id: navId })}
    onEdit={openEdit}
    onOpenSite={(siteId, siteVerticalId) => route.push({ tab: 'sites', id: siteId, vertical: String(siteVerticalId) })}
  />
{:else}
  <div class="tym-page">
    {#if !ownersAvailable}
      <div class="tym-card flex flex-col items-center justify-center px-6 py-12 text-center">
        <span
          class="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl"
          style="background:#FBF0DC; color:#8a5c00"
        >
          <Landmark class="h-6 w-6" />
        </span>
        <div class="font-heading text-base font-bold text-navy">Select the Hotels vertical</div>
        <div class="mt-1.5 max-w-[360px] text-[13px] text-dark-gray">
          Owners is a Hotels-only view — pick Hotels from the switcher above.
        </div>
      </div>
    {:else}
      <div class="mb-4 flex flex-wrap items-center gap-2">
        <span class="flex-1"></span>
        <span class="text-[12.5px] text-dark-gray">{listQuery.data?.count ?? 0} owners</span>
      </div>

      <SearchBar bind:value={search} onCreate={openCreate} createLabel="New owner" />

      {#if listQuery.isLoading}
        <p class="tym-loading-state">Loading owners&hellip;</p>
      {:else if listQuery.isError}
        <p class="tym-error-state">{getErrorMessage(listQuery.error)}</p>
      {:else if listQuery.data}
        <!-- desktop/tablet table; below `sm` it gives way to the card list -->
        <div class="tym-table-wrap tym-shadow hidden sm:block">
          <div class="tym-table-scroll">
            <table class="tym-table">
              <thead>
                <tr>
                  <th>Owner</th>
                  <th>Sites</th>
                  <th class="num"></th>
                </tr>
              </thead>
              <tbody>
                {#each listQuery.data.results as row (row.id)}
                  <tr class="clickable" onclick={() => openDetail(row)}>
                    <td>
                      <div class="flex items-center gap-[11px]">
                        <span
                          class="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-[9px]"
                          style="background:#FBF0DC; color:#8a5c00"
                        >
                          <Landmark class="h-4 w-4" />
                        </span>
                        <span class="block text-[13.5px] font-semibold text-navy">{row.name}</span>
                      </div>
                    </td>
                    <td class="muted">{row.site_count ?? 0}</td>
                    <td class="num">
                      <div class="flex justify-end gap-1.5">
                        <button
                          type="button"
                          class="btn-secondary !h-10 !w-10 !p-0"
                          onclick={(e) => {
                            e.stopPropagation();
                            openEdit(row);
                          }}
                          aria-label={`Edit ${row.name}`}
                        >
                          <Pencil class="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          class="btn-danger !h-10 !w-10 !p-0"
                          onclick={(e) => {
                            e.stopPropagation();
                            remove(row);
                          }}
                          aria-label={`Delete ${row.name}`}
                        >
                          <Trash2 class="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                {:else}
                  <tr><td class="empty" colspan="3">No owners found.</td></tr>
                {/each}
              </tbody>
            </table>
          </div>
        </div>

        <!-- mobile card list — the row's edit/delete buttons need a real tap
             target, which a 3-column table can't give them at 390px -->
        <div class="flex flex-col gap-2.5 sm:hidden">
          {#each listQuery.data.results as row (row.id)}
            <div class="tym-list-card !cursor-default">
              <button
                type="button"
                class="flex w-full items-center gap-3 text-left"
                onclick={() => openDetail(row)}
              >
                <span
                  class="flex h-10 w-10 shrink-0 items-center justify-center rounded-[11px]"
                  style="background:#FBF0DC; color:#8a5c00"
                >
                  <Landmark class="h-[18px] w-[18px]" />
                </span>
                <span class="min-w-0 flex-1">
                  <span class="block truncate text-[15px] font-semibold leading-[1.35] text-navy">{row.name}</span>
                  <span class="mt-0.5 block text-[12px] text-dark-gray"
                    >{row.site_count ?? 0} site{(row.site_count ?? 0) === 1 ? '' : 's'}</span
                  >
                </span>
              </button>
              <div class="mt-3 flex gap-2 border-t border-[#F0F0F0] pt-3">
                <button type="button" class="btn-secondary flex-1" onclick={() => openEdit(row)}>
                  <Pencil class="h-4 w-4" /> Edit
                </button>
                <button type="button" class="btn-danger flex-1" onclick={() => remove(row)}>
                  <Trash2 class="h-4 w-4" /> Delete
                </button>
              </div>
            </div>
          {:else}
            <p class="tym-empty-state">No owners found.</p>
          {/each}
        </div>

        <Pagination
          page={listQuery.data.page}
          perPage={listQuery.data.per_page}
          count={listQuery.data.count}
          onChange={(p) => (page = p)}
        />
      {/if}
    {/if}
  </div>
{/if}

{#if showForm}
  <CompanyFormModal
    editing={editingOwner}
    defaultKind="owner"
    lockKind={true}
    defaultVerticalId={verticalId}
    lockVertical={true}
    onClose={closeForm}
  />
{/if}
